import dayjs from 'dayjs'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import styled from 'styled-components'

const ISO = 'YYYY-MM-DD'

export type DateRangeEdge = 'from' | 'to'

interface Props {
  month: dayjs.Dayjs
  onMonthChange: (month: dayjs.Dayjs) => void
  edge: DateRangeEdge
  from?: string
  to?: string
  onPickDay: (value: string) => void
}

export const DateRangeCalendar: React.FC<Props> = ({
  month,
  onMonthChange,
  edge,
  from,
  to,
  onPickDay
}) => {
  const [t] = useTranslation()

  const days = useMemo(() => {
    const first = month.startOf('month')
    const lead = (first.day() + 6) % 7
    const length = month.daysInMonth()
    const cells: ({ value: string; label: string } | null)[] = Array(lead).fill(null)
    for (let day = 1; day <= length; day++) {
      const value = first.date(day).format(ISO)
      cells.push({ value, label: String(day) })
    }
    return cells
  }, [month])

  return (
    <Calendar>
      <CalendarHeader>
        <CalendarNav
          type="button"
          onClick={() => onMonthChange(month.subtract(1, 'month'))}
          aria-label={t('components.dateRangeFilter.previousMonth')}
        >
          <FaChevronLeft />
        </CalendarNav>
        <CalendarTitle>{month.format('MMMM YYYY')}</CalendarTitle>
        <CalendarNav
          type="button"
          onClick={() => onMonthChange(month.add(1, 'month'))}
          aria-label={t('components.dateRangeFilter.nextMonth')}
        >
          <FaChevronRight />
        </CalendarNav>
      </CalendarHeader>
      <CalendarHint>
        {t(
          edge === 'from'
            ? 'components.dateRangeFilter.pickStart'
            : 'components.dateRangeFilter.pickEnd'
        )}
      </CalendarHint>
      <CalendarGrid>
        {(
          t('components.dateRangeFilter.weekDays', {
            returnObjects: true
          }) as string[]
        ).map(day => (
          <WeekDay key={day}>{day}</WeekDay>
        ))}
        {days.map((cell, index) =>
          cell === null ? (
            <DayCell key={`lead-${index}`} as="span" $lead />
          ) : (
            <DayCell
              key={cell.value}
              type="button"
              $edge={cell.value === from || cell.value === to}
              $inRange={Boolean(from && to && cell.value > from && cell.value < to)}
              onClick={() => onPickDay(cell.value)}
            >
              {cell.label}
            </DayCell>
          )
        )}
      </CalendarGrid>
    </Calendar>
  )
}

const Calendar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => theme.dashboard.surfaceSubtle};
`

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const CalendarNav = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.625rem;
  height: 1.625rem;
  border: none;
  border-radius: 0.4375rem;
  background: transparent;
  color: ${({ theme }) => theme.dashboard.textSubtle};
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.neutralGray};
  }
`

const CalendarTitle = styled.span`
  flex: 1;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.menuBg};
`

const CalendarHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.px10}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`

const WeekDay = styled.span`
  padding: 2px 0;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.dashboard.onNavyMuted};
`

const DayCell = styled.button<{ $edge?: boolean; $inRange?: boolean; $lead?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 1.75rem;
  border: none;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  cursor: ${({ $lead }) => ($lead ? 'default' : 'pointer')};
  border-radius: ${({ $edge, $inRange }) => ($inRange && !$edge ? '0' : '0.4375rem')};
  background: ${({ theme, $edge, $inRange }) =>
    $edge ? theme.colors.primary : $inRange ? theme.dashboard.infoTint : 'transparent'};
  color: ${({ theme, $edge, $lead }) =>
    $lead ? 'transparent' : $edge ? theme.colors.neutralWhite : theme.colors.metalGray};
  font-weight: ${({ $edge }) => ($edge ? 700 : 500)};

  &:hover:not(:disabled) {
    background: ${({ theme, $edge }) => ($edge ? theme.colors.primary : theme.dashboard.infoTint)};
  }
`
